import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { bestFaresOptions } from "@jetblue/dotcom-api/best-fares";
import { getMonthsBetween } from "@jetblue/kit/calendar";
import type {
  BestFareByDate,
  BestFaresRequest,
} from "@jetblue/dotcom-api/best-fares";
import type { CalendarDate } from "@internationalized/date";
import { TIMEZONE } from "../timezone";
import type { PassengerCounts } from "../use-passengers";
import type { TripType } from "./use-flight-search-form";

export type UseBestFaresCalendarInput = {
  destination?: string;
  endDate: CalendarDate;
  origin?: string;
  passengers: PassengerCounts;
  showDatePickerPricing: boolean;
  startDate: CalendarDate;
  throwOnError?: boolean;
  tripType: TripType;
  usePoints: boolean;
};
export type BestFaresCalendarQueryInput = Pick<
  UseBestFaresCalendarInput,
  "destination" | "origin" | "passengers" | "usePoints"
>;
export type BestFaresPriceMaps = {
  outbound: Map<string, BestFareByDate>;
  inbound: Map<string, BestFareByDate>;
  currencyCode?: string;
};
type MonthlyMins = {
  outbound: Map<string, number>;
  inbound: Map<string, number>;
};
type UseBestFaresCalendarResult = {
  prices: BestFaresPriceMaps;
  monthlyMins: MonthlyMins;
  isError: boolean;
  isLoading: boolean;
};

const MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  timeZone: TIMEZONE,
  year: "numeric",
});

const formatMonth = (month: CalendarDate) =>
  MONTH_FORMATTER.format(month.toDate(TIMEZONE)).toLowerCase();

const toBestFaresRequest = (
  month: string,
  input: Pick<
    UseBestFaresCalendarInput,
    "destination" | "origin" | "tripType" | "usePoints"
  > &
    PassengerCounts,
): BestFaresRequest => {
  const {
    adults,
    children,
    destination,
    infants,
    origin,
    tripType,
    usePoints,
  } = input;
  return {
    adult: String(adults),
    child: String(children),
    destination: destination ?? "",
    fareType: usePoints ? "POINTS" : "LOWEST",
    infant: String(infants),
    month,
    origin: origin ?? "",
    tripType: tripType === "round-trip" ? "RETURN" : "ONE_WAY",
  };
};

const combineMonthQueryResults = <TData>(results: UseQueryResult<TData>[]) => ({
  isError: results.some((result) => result.isError),
  isLoading: results.some((result) => result.isLoading),
  responses: results.flatMap((result) =>
    result.data === undefined ? [] : [result.data],
  ),
});

export const useBestFaresCalendar = (
  input: UseBestFaresCalendarInput,
): UseBestFaresCalendarResult => {
  const {
    destination,
    endDate,
    origin,
    passengers: { adults, children, infants },
    showDatePickerPricing,
    startDate,
    throwOnError,
    tripType,
    usePoints,
  } = input;

  const months = useMemo(
    () => getMonthsBetween(startDate, endDate),
    [startDate, endDate],
  );

  const requests = useMemo(
    () =>
      months.map((month) =>
        toBestFaresRequest(formatMonth(month), {
          adults,
          children,
          destination,
          infants,
          origin,
          tripType,
          usePoints,
        }),
      ),
    [
      months,
      origin,
      destination,
      tripType,
      usePoints,
      adults,
      children,
      infants,
    ],
  );

  const monthQueryState = useQueries({
    combine: combineMonthQueryResults,
    queries: requests.map((request) => ({
      ...bestFaresOptions(request),
      enabled: Boolean(showDatePickerPricing && origin && destination),
      throwOnError,
    })),
  });

  const { prices, monthlyMins } = useMemo<{
    prices: BestFaresPriceMaps;
    monthlyMins: MonthlyMins;
  }>(() => {
    const outboundPrice = new Map<string, BestFareByDate>();
    const inboundPrice = new Map<string, BestFareByDate>();
    const outboundMonthlyMins = new Map<string, number>();
    const inboundMonthlyMins = new Map<string, number>();

    let currencyCode: string | undefined;

    const recordMonthlyMin = (
      map: Map<string, number>,
      date: string,
      amount: number,
    ) => {
      const monthKey = date.slice(0, 7);
      const current = map.get(monthKey);
      if (current === undefined || amount < current) {
        map.set(monthKey, amount);
      }
    };

    const ingestFares = (
      faresByDate: Record<string, BestFareByDate> | undefined,
      targetFares: Map<string, BestFareByDate>,
      targetMonthlyMins: Map<string, number>,
    ) => {
      if (!faresByDate) {
        return;
      }
      for (const [date, fare] of Object.entries(faresByDate)) {
        if (fare.seats <= 0) {
          continue;
        }
        targetFares.set(date, fare);
        recordMonthlyMin(targetMonthlyMins, date, fare.amount);
      }
    };

    for (const response of monthQueryState.responses) {
      if (!currencyCode && response.currencyCode) {
        currencyCode = response.currencyCode;
      }
      ingestFares(
        response.outboundFaresByDate,
        outboundPrice,
        outboundMonthlyMins,
      );

      ingestFares(
        response.inboundFaresByDate,
        inboundPrice,
        inboundMonthlyMins,
      );
    }
    return {
      monthlyMins: {
        inbound: inboundMonthlyMins,
        outbound: outboundMonthlyMins,
      },
      prices: {
        currencyCode,
        inbound: inboundPrice,
        outbound: outboundPrice,
      },
    };
  }, [monthQueryState.responses]);

  return {
    isError: monthQueryState.isError,
    isLoading: monthQueryState.isLoading,
    monthlyMins,
    prices,
  };
};
